import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Able, CartItem, OrderInformation, PaymentInfo, ProductSelection, ShippingRateInfo } from '../../app.models';
import { AppSandbox } from '../../app.sandbox';
import { ASSET_BASE_URL } from '../../app.data.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import to from 'await-to-js';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Router } from '@angular/router';

@Component({
  selector: 'app-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss']
})
export class ShoppingCartComponent implements OnInit, OnChanges {
  BASE_MEDIA_URL = ASSET_BASE_URL + '/medium';

  @Input() hideHeader = false;
  @Input() orderInformation: OrderInformation;
  @Input() showComplete = false;
  @Input() processingPayment = false;
  @Input() isEditingPayment = false; // Add this
  @Output() completed = new EventEmitter<{
    cart: CartItem[]
    shippingRate: ShippingRateInfo,
    subtotal: number;
    total: number;
  }>();

  @Input() error: string;
  calculatingShipping = false;
  cart: CartItem[] = [];
  // shippingInfo: ShippingInfo;
  shippingRates: ShippingRateInfo[];
  selectedShippingRate: ShippingRateInfo;
  showRate = true;
  noShippableItems = false;
  salesTax = null;
  salesTaxMessage = null;
  handlingCost = null;
  currentRoute = '';

  constructor(private sandbox: AppSandbox, private httpClient: HttpClient, private sanitize: DomSanitizer, private router: Router) { }

  async ngOnInit() {
    this.currentRoute = this.router.url;
    this.sandbox.cart$.subscribe(async (cart) => {
      if (!!cart) {
      //console.log('cart is', cart);
        this.cart = cart;
      } else {
      //console.log('cart is empty');
        this.cart = [];
      }

      await this.calculateAll();
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
  //console.log('have changes', changes);
    if (!!changes && !!changes['showComplete'] && changes['showComplete'].currentValue === true) {
    //console.log('showComplete changed');
      await this.calculateAll();
    }
  }

  sortFields(fields: [{
    name: string;
    type: string;
    order: number;
    required?: boolean;
  }]) {
    // console.log('sortFields', fields);
    const copy = [].concat(fields);

    return copy.sort((a, b) => {
      if (a.order < b.order) {
        return -1;
      } else if (a.order > b.order) {
        return 1;
      }
      return 0;
    });
  }

  async calculateHandling() {
    let handlingCost = 0;
    this.cart.forEach(item => {
    //console.log('checking handling fees for product', item);
      if (!!item.product.handling_price && item.product.handling_price > handlingCost) {
        handlingCost += item.product.handling_price;
      }
    });
    this.handlingCost = handlingCost;
  }

  async calculateShipping() {
  //console.log('calculating shipping');
    if (!this.orderInformation || !this.showComplete) {
    //console.log('no order information or not showing complete', this.orderInformation, this.showComplete);
      return;
    }

    this.selectedShippingRate = null;
    this.shippingRates = null;
    this.calculatingShipping = true;
    this.noShippableItems = false;
    this.showRate = true;

    try {
      this.error = null;

      // get weight of all items
      const data = {
        name: `${this.orderInformation.firstName} ${this.orderInformation.lastName}`,
        address1: this.orderInformation.address1,
        address2: this.orderInformation.address2,
        city: this.orderInformation.city,
        state: this.orderInformation.state,
        zip: this.orderInformation.zip,
        parcels: []
      };

    //console.log('calculateShipping(): shipping data', data);

    //console.log('calculateShipping(): checking cart items', this.cart);

      this.cart.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          if (item.product.shippable === true && (!item.product.shipping_free)) {
          //console.log('is shipping free on item', item.product.shipping_free);
            data.parcels.push({
              weight: item.product.weight
            });
          }
        }
      });

      if (data.parcels.length === 0) {
        // we do not have shippable products
        this.noShippableItems = true;
        return;
      }

    //console.log('calculating shipping for parcels', data);

      let result: HttpResponse<ShippingRateInfo[]>;
      let err;
      [err, result] = await to(this.httpClient.post<ShippingRateInfo[]>('/.netlify/functions/calculate-shipping',
        data, { observe: 'response' }).toPromise());

      if (!err && !!result && result.status === 200) {

        if (result.body.length > 0) {
          // we're good
          // this.shippingInfo = result.body;
          this.shippingRates = result.body; // just pick the first entry for now, should be no choices
        //console.log('shipping rates', this.shippingRates);
        } else {
          console.error('no rates returned');
          this.error = 'Unable to calculate shipping for destination. Please <a href="/market-office">contact support</a> to complete your order';
        }

        // {"rates":[{
        //   "object_created":"2019-10-31T05:15:07.493Z",
        //   "object_id":"915f6c4bf149485ea6f77bfb37372775",
        //   "object_owner":"david@lavisual.com",
        //   "shipment":"928156bd58644c738f711410e3951934",
        //   "attributes":["BESTVALUE","CHEAPEST","FASTEST"],
        //   "amount":"17.60",
        //   "currency":"USD",
        //   "amount_local":"17.60",
        //   "currency_local":"USD",
        //   "provider":"USPS",
        //   "provider_image_75":"https://shippo-static.s3.amazonaws.com/providers/75/USPS.png",
        //   "provider_image_200":"https://shippo-static.s3.amazonaws.com/providers/200/USPS.png",
        //   "servicelevel":{"name":"Priority Mail","token":"usps_priority","terms":""},
        //   "estimated_days":2,"arrives_by":null,
        //   "duration_terms":"Delivery within 1, 2, or 3 days based on where your package started and where it’s being sent.",
        //   "messages":[],"carrier_account":"a105198576b6473da831fb94ed784642","test":true,"zone":"1"}]
      } else {
        console.error('shipping cannot be calculated', !!result ? result.status : null, !!result ? result.body : null, err);
        this.error = 'Unable to calculate shipping';
      }
    } catch (err) {
      console.error('unknown error calculating shipping', err);
      this.error = 'Unknown error calculating shipping';
    } finally {
      this.calculatingShipping = false;
    }
  }

  async calculateSalesTax() {
    if (!this.orderInformation || !this.showComplete) {
      return;
    }

    const selections = this.cart.map(item => {
      return {
        product: item.product,
        quantity: item.quantity,
        upc: item.upc,
      } as ProductSelection;
    });

    const data = {
      state: this.orderInformation.state,
      productSelections: selections
    };

    const [err, result] = await to(this.httpClient.post('/.netlify/functions/calculate-taxes', data, { observe: 'response' }).toPromise());

    if (!!err && !!result && result.status !== 200) {
      const body = result.body as any;
      if (!!body) {
        console.error('error calculating taxes', body);
        this.error = body.message;
        return null;
      }

      this.error = 'Unknown error calculating taxes';
      return false;
    } else if (err) {
      this.error = 'Unknown error calculating taxes';
      return null;
    }

    const taxes = result.body['taxes'];
    const message = result.body['message'];

    this.salesTax = taxes;
    this.salesTaxMessage = message;
  }

  removeItem(item: CartItem) {
    this.sandbox.removeItemFromCart(item);
  }

  async changeQuantity(item: CartItem, quantity: number) {
  //console.log('quantity now', quantity);
    this.sandbox.updateItemInCart(item, quantity);

    await this.calculateAll();
  }

  getSubTotal() {
    let total = 0;
    this.cart.forEach(o => {
      total += ((o.product.price * 100) * o.quantity) / 100;
    });
    return total;
  }

  getTotal() {
    let total = this.getSubTotal();
    total += !!this.selectedShippingRate ? parseFloat(this.selectedShippingRate.amount) : 0;
    total += this.handlingCost;
    total += !!this.salesTax ? parseFloat(this.salesTax) : 0;
    return total;
  }

  complete() {
  //console.log('clicked complete');

    // FIXME - complete the order here
    this.completed.emit({
      cart: this.cart,
      shippingRate: this.selectedShippingRate,
      subtotal: this.getSubTotal(),
      total: this.getTotal(),
    });

    // this.errors.emit(new Error('Some Error'));
  }

  async calculateAll() {
    this.error = null;
    await this.calculateHandling();
    await this.calculateShipping();
    await this.calculateSalesTax();
  }

  // trackBySku(index: number, item: CartItem) {
  //   return 'sku' + item.product.id;
  // }

  safeStyle(style: string): SafeStyle {
    return this.sanitize.bypassSecurityTrustStyle(style);
  }

}
